"use client"

import { useAuth } from "@/features/auth"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { useLicense } from '@/hooks/useLicense'
import { cn } from "@/lib/utils"
import { supabase } from "@/services/supabase/supabase"
import {
  LogOut,
  User,
  Zap,
  ChevronRight,
  Settings
} from "lucide-react"

import { SubscriptionManager } from "@/components/payment/subscription-manager"
import { AnimatePresence, motion } from "framer-motion"

// Tab definitions
const TABS = [
  { id: 'profile', label: 'Profile Settings', icon: User, description: 'Manage your personal details' },
  { id: 'subscription', label: 'Plan & Billing', icon: Zap, description: 'Manage subscription and credits' },
] as const;

type TabId = typeof TABS[number]['id'];

export default function AccountPage() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const { licenseDetails } = useLicense();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [name, setName] = useState("");

  // Auth & Data loading
  useEffect(() => {
    if (!session?.user) {
      navigate("/auth");
      return;
    }
    const user = session.user;
    setName(user.user_metadata?.full_name || user.email?.split("@")[0] || "");
  }, [session, navigate]);

  const user = session?.user;
  if (!user) return null;

  const firstName = name?.split(' ')[0] || "Author";
  const userInitials = firstName.substring(0, 2).toUpperCase();
  const currentPlan = licenseDetails?.plan || 'Free';


  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      toast({ title: "Sign out failed", variant: "destructive" });
    }
  };

  // Logic: Update Profile (Name only for now)
  const handleSaveProfile = async () => {
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: name }
      });
      if (error) throw error;
      toast({ title: "Profile Updated", description: "Your profile details have been saved." });
    } catch (error) {
      toast({ title: "Update Failed", description: "Could not update profile.", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans selection:bg-primary/20">
      {/* Premium minimal header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40"
      >
        <div className="container max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/workspace')} className="gap-2 text-muted-foreground hover:text-foreground pl-0 hover:bg-transparent">
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to Workspace
          </Button>
        </div>
      </motion.header>

      <main className="flex-1 container max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border/40 pb-8"
        >
          <div>
            <div className="flex items-center gap-3 text-muted-foreground mb-2">
              <Settings className="h-4 w-4" />
              <span className="text-sm font-medium">Account Settings</span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground">
              Settings & Billing
            </h1>
            <p className="text-muted-foreground text-lg mt-2 max-w-2xl">
              Manage your personal information, subscription plan, and usage credits.
            </p>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-full border border-border/50 backdrop-blur-sm">
            <div className={`h-2.5 w-2.5 rounded-full ${currentPlan !== 'Free' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]' : 'bg-muted-foreground'}`} />
            <span className="text-sm font-medium">
              {currentPlan} Plan <span className="text-muted-foreground font-normal">Active</span>
            </span>
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="w-full lg:w-64 flex-shrink-0 space-y-6">
            <div className="px-4 py-4 rounded-xl border border-border/40 bg-card/30 space-y-3">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border border-border">
                  <AvatarImage src={user.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">{userInitials}</AvatarFallback>
                </Avatar>
                <div className="overflow-hidden">
                  <h3 className="font-medium text-sm text-foreground truncate">{name || "Author"}</h3>
                  <p className="text-xs text-muted-foreground truncate opacity-80">{user.email}</p>
                </div>
              </div>
            </div>

            <nav className="space-y-1">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200",
                      isActive
                        ? "bg-secondary/50 text-foreground"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    )}
                  >
                    <Icon className={cn("h-4 w-4", isActive ? "text-foreground" : "text-muted-foreground")} />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="max-w-4xl"
              >
                {/* Profile Details Tab */}
                {activeTab === 'profile' && (
                  <div className="space-y-6">
                    <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-sm overflow-hidden">
                      <CardHeader className="border-b border-border/40 bg-muted/10 pb-4">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <User className="h-5 w-5 text-primary" />
                          Personal Information
                        </CardTitle>
                        <CardDescription>Update your public profile details.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6 pt-6">
                        <div className="grid gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-xs uppercase text-muted-foreground tracking-wider font-semibold">Display Name</Label>
                            <Input
                              id="name"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              className="bg-background/50 border-input transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 h-10"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs uppercase text-muted-foreground tracking-wider font-semibold">Email Address</Label>
                            <Input
                              id="email"
                              value={user.email || ""}
                              disabled
                              className="bg-muted/20 border-border/50 text-muted-foreground h-10"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end pt-2">
                          <Button onClick={handleSaveProfile} className="min-w-[120px]">Save Changes</Button>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-red-500/20 bg-red-500/5 overflow-hidden">
                      <CardContent className="p-6 flex items-center justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-red-500 flex items-center gap-2">
                            <LogOut className="h-4 w-4" />
                            Sign Out
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            Securely log out of your account on this device.
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => handleSignOut()}
                          className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                        >
                          Log Out
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Subscription Tab */}
                {activeTab === 'subscription' && (
                  <div className="space-y-8">
                    <SubscriptionManager />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  )
}